import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';
import type {VariantProps} from 'class-variance-authority';
import {cva} from 'class-variance-authority';
import {twMerge} from 'tailwind-merge';

const button = cva(
	[
		'no-underline',
		'inline-flex',
		'items-center',
		'justify-center',
		'gap-2',
		'shrink-0',
		'rounded-[5px]',
		'font-bold',
		'text-base',
		'border-2',
		'transition-colors',
	],
	{
		variants: {
			variant: {
				primary: [
					'bg-primary',
					'data-[theme=dark]:bg-white',
					'hover:bg-primary',
					'data-[theme=dark]:hover:bg-primary',
					'active:bg-primary-dark',
					'data-[theme=dark]:active:bg-primary-dark',

					'border-primary',
					'data-[theme=dark]:border-white',
					'hover:border-primary',
					'data-[theme=dark]:hover:border-primary',
					'active:border-primary-dark',
					'data-[theme=dark]:active:border-primary-dark',

					'text-white',
					'data-[theme=dark]:text-black',
					'data-[theme=dark]:hover:text-white',
					'data-[theme=dark]:active:text-white',

					'disabled:bg-disabled',
					'disabled:border-disabled',
					'data-[theme=dark]:disabled:text-[#4B4642]',
					'data-[theme=dark]:disabled:border-disabled',
					'data-[theme=dark]:disabled:bg-disabled',
				],
				secondary: [
					'bg-white',
					'data-[theme=dark]:bg-transparent',

					'border-[#615E5C]',
					'hover:border-primary',
					'active:border-primary-dark',
					'data-[theme=dark]:hover:border-primary',
					'data-[theme=dark]:active:border-primary-dark',

					'disabled:border-disabled',
					'disabled:text-disabled',

					'text-black',
					'hover:text-primary',
					'data-[theme=dark]:hover:text-primary',
					'active:text-primary-dark',
					'data-[theme=dark]:active:text-primary-dark',
					'data-[theme=dark]:text-white',
					'data-[theme=dark]:border-white',
					'data-[theme=dark]:active:border-primary-dark',
					'data-[theme=dark]:hover:border-primary',
				],
				tertiary: [
					'border-transparent',
					'underline',
					'hover:no-underline',
					'hover:text-primary',
					'active:text-primary-dark',
					'active:underline',
					'disabled:text-disabled',
					'disabled:underline',
					'text-black',
				],
			},
			size: {
				medium: ['px-[23px]', 'py-[10px]'],
				large: ['px-[28px]', 'py-[16px]'],
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'medium',
		},
	},
);

export type ButtonProps = VariantProps<typeof button> &
	ComponentPropsWithoutRef<'button'> & {
		/**
		 * Change the default rendered element for the one passed as a child, merging their props and behavior.
		 * @default false
		 */
		asChild?: boolean;
	};

/**
 * Button used for interaction
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({children, variant = 'primary', size = 'medium', type, className, asChild, ...rest}, ref) => {
		const Component = asChild ? Slot : 'button';

		return (
			<Component
				ref={ref}
				type={type}
				className={twMerge(button({variant, size, className}))}
				{...rest}
			>
				{children}
			</Component>
		);
	},
);

Button.displayName = 'Button';
